import { Dimension, TableCell, TableValue } from "./types";

/**
 * Creates an option matrix based on the given dimensions and axis.
 * @param dimensions - The dimensions for the option matrix.
 * @param axis - The axis for which to create the option matrix ("x" or "y").
 * @returns The generated option matrix.
 */
export function createOptionMatrix(
  dimensions: Dimension[],
  axis: "x" | "y",
): TableCell[][] {
  let matrix: TableCell[][] = [];

  // Helper functions to calculate the row and column multipliers
  const rowMultiplier =
    axis === "y" ? (i: number) => dimMultiplier(dimensions, i) : () => 1;
  const colMultiplier =
    axis === "x" ? (i: number) => dimMultiplier(dimensions, i) : () => 1;

  // Generate combinations
  generateCombinations(matrix, dimensions, rowMultiplier, colMultiplier, 0, []);

  // Transpose the matrix if the axis is the x-axis
  if (axis === "x") {
    matrix = (matrix[0] ?? []).map((_, colIndex) =>
      matrix.map((row) => row[colIndex]),
    );
  }

  // Hide spanned cells
  hideSpannedCells(matrix);

  return matrix;
}

/**
 * Calculates the multiplier for a dimension at the given index.
 * @param dimensions - The dimensions.
 * @param index - The index of the dimension.
 * @returns The multiplier for the dimension.
 */
function dimMultiplier(dimensions: Dimension[], index: number): number {
  // Row span: depends on the number of options in the subsequent dimensions
  const dimSize = dimensions
    .slice(index + 1)
    .reduce((acc, dim) => acc * dim.values.length, 1);
  return dimSize || 1;
}

/**
 * Generates combinations for the option matrix.
 * @param matrix - The option matrix.
 * @param dimensions - The dimensions.
 * @param rowMultiplier - The row multiplier function.
 * @param colMultiplier - The column multiplier function.
 * @param dimIndex - The index of the current dimension.
 * @param combination - The current combination.
 */
function generateCombinations(
  matrix: TableCell[][],
  dimensions: Dimension[],
  rowMultiplier: (index: number) => number,
  colMultiplier: (index: number) => number,
  dimIndex: number,
  combination: TableCell[],
) {
  if (dimIndex === dimensions.length) {
    // Add a full row to the matrix
    matrix.push(combination.slice());
    return;
  }

  const dimension = dimensions[dimIndex];
  const dimensionLength = dimension.values.length;
  for (let i = 0; i < dimensionLength; i++) {
    const cell: TableCell = {
      ...dimension.values[i],
      nRows: rowMultiplier(dimIndex),
      nCols: colMultiplier(dimIndex),
      ids: [...combination.map(({ id }) => id), dimension.values[i].id],
      show: true,
    };

    combination.push(JSON.parse(JSON.stringify(cell)));
    generateCombinations(
      matrix,
      dimensions,
      rowMultiplier,
      colMultiplier,
      dimIndex + 1,
      combination,
    );
    combination.pop();
  }
}

/**
 * Hides spanned cells in the option matrix.
 * @param matrix - The option matrix.
 * @returns The modified option matrix.
 */
function hideSpannedCells(matrix: TableCell[][]): TableCell[][] {
  // Hide spanned cells
  for (let rowIndex = 0; rowIndex < matrix.length; rowIndex++) {
    const row = matrix[rowIndex];
    for (let cellIndex = 0; cellIndex < row.length; cellIndex++) {
      const cell = row[cellIndex];
      if (!cell || cell.show === false) {
        continue;
      }

      if (cell.nRows > 1) {
        for (let i = 1; i < cell.nRows; i++) {
          if (
            matrix[rowIndex + i] &&
            matrix[rowIndex + i][cellIndex] &&
            matrix[rowIndex + i][cellIndex].id === cell.id
          ) {
            matrix[rowIndex + i][cellIndex] = {
              ...matrix[rowIndex + i][cellIndex],
              show: false,
            };
          }
        }
      } else if (cell.nCols > 1) {
        for (let i = 1; i < cell.nCols; i++) {
          if (
            matrix[rowIndex][cellIndex + i] &&
            matrix[rowIndex][cellIndex + i].id === cell.id
          ) {
            matrix[rowIndex][cellIndex + i] = {
              ...matrix[rowIndex][cellIndex + i],
              show: false,
            };
          }
        }
      }
    }
  }

  return matrix;
}

/**
 * Calculates the number of rows in the option matrix.
 * @param yAxis - The y-axis object containing the dimensions.
 * @returns The number of rows in the option matrix.
 */
export function nRows(yAxis: { dimensions: Dimension[] }): number {
  return yAxis.dimensions.reduce((acc: number, dimension: Dimension) => {
    return acc * dimension.values.length;
  }, 1);
}

/**
 * Calculates the number of columns in the option matrix.
 * @param xAxis - The x-axis object containing the dimensions.
 * @returns The number of columns in the option matrix.
 */
export function nCols(xAxis: { dimensions: Dimension[] }): number {
  return xAxis.dimensions.reduce((acc: number, dimension: Dimension) => {
    return acc * dimension.values.length;
  }, 1);
}

/**
 * Builds a matrix based on the x-axis and y-axis option matrices.
 * @param xAxis - The x-axis object containing the dimensions and option matrix.
 * @param yAxis - The y-axis object containing the dimensions and option matrix.
 * @returns The built matrix.
 */
export function createValueMatrix(
  xAxis: {
    dimensions: Dimension[];
    optionMatrix: TableCell[][];
  },
  yAxis: {
    dimensions: Dimension[];
    optionMatrix: TableCell[][];
  },
): TableValue[][] {
  // Create a matrix based on the option matrices
  const matrix = [];

  // Get the dimension ids for the x and y axes
  const xAxisDimensionIds = xAxis.dimensions.map((dim) => dim.id);
  const yAxisDimensionIds = yAxis.dimensions.map((dim) => dim.id);
  for (let i = 0; i < nRows(yAxis); i++) {
    const row = [];
    for (let j = 0; j < nCols(xAxis); j++) {
      const yAxisOptionIds = yAxis.optionMatrix[i].map((cell) => cell?.id);
      // Get the option ids for the current row and column
      const xAxisOptionIds = xAxis.optionMatrix.map((row) => row[j]?.id);

      // Build attributes
      const attributes = {
        ...Object.fromEntries(
          xAxisDimensionIds.map((id, index) => [id, xAxisOptionIds[index]]),
        ),
        ...Object.fromEntries(
          yAxisDimensionIds.map((id, index) => [id, yAxisOptionIds[index]]),
        ),
      };

      row.push({
        attributes,
        value: {
          textValue: "",
          floatValue: 0,
        },
      });
    }

    // Add the row to the matrix
    matrix.push(row);
  }

  return matrix;
}

/**
 * Pivots a matrix
 *
 * @param {(string | number)[][]} matrix - The 2D array to pivot.
 * @returns {(string | number)[][]} - The pivoted 2D array.
 */
export const pivotMatrix = (
  matrix: (string | number)[][],
): (string | number)[][] => {
  const numRows = matrix.length;
  const numCols = matrix[0].length;

  const pivotedMatrix = new Array(numCols);
  for (let i = 0; i < numCols; i++) {
    pivotedMatrix[i] = new Array(numRows);
    for (let j = 0; j < numRows; j++) {
      pivotedMatrix[i][j] = matrix[j][i];
    }
  }

  return pivotedMatrix;
};

export function isTableData(content: string): [boolean, string] {
  // Detect if the pasted content is a table
  const containsTabsOrSpaces = /\t| {2,}/.test(content);
  let isTable = containsTabsOrSpaces;

  // Check if pasted content contains multiple dollar or euro signs
  const containsMultipleCurrencySigns =
    (content.match(/[$€]/g) || []).length > 1;
  if (!containsTabsOrSpaces && containsMultipleCurrencySigns) {
    // Replace all dollar signs with euro signs
    content.replace(/[$]/g, "€");

    // Replace all euro signs with enters
    content.replace(/€/g, "\n");

    // Handle as a table
    isTable = true;
  }

  return [isTable, content];
}

export function stringToTableData(content: string): (string | number)[][] {
  // Assuming the content is a table
  let tableData: (string | number)[][] = [];
  const lines = content.trim().split("\n");

  lines.forEach((line: string) => {
    // Split the line using tabs or multiple spaces as delimiters
    const rowData = line.trim().split(/\t| {2,}/);

    // Process each cell in the row
    const processedRowData = rowData.map((cell) => {
      // Remove non-numeric characters except dots and convert commas to dots
      const processedCell = cell.replace(/[^0-9.,]/g, "").replace(",", ".");

      // Parse the cell to a float
      const floatCell = parseFloat(processedCell);

      // Return the float value or null if the cell doesn't contain a number
      return isNaN(floatCell) ? "" : floatCell;
    });

    // Add the processed row data to the table
    tableData.push(processedRowData);
  });

  return tableData;
}
